import { useEffect, useMemo, useRef, useState } from "react";
import { useHasAccess } from "@/components/common/has-access";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { GripVertical, LayoutGrid, X } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  setRegionConfigPriorities,
  useRegionConfigs,
} from "@/features/products/product-region-config-api";

type Item = { id: string; name: string; thumbnail: string | null };

// The dashboard product order, as a toolbar control. Collapsed, it is a compact
// button in the page header; clicking drops a small phone-frame panel beneath
// it where the region's launched+visible products can be dragged to reorder —
// renumbered 1..N and autosaved via the bulk priorities endpoint. Native HTML5
// drag (no dependency).
//
// It used to float fixed in the bottom-right corner, which is exactly where the
// table's row actions end up on the last rows, so the buttons under it could
// not be clicked. Anchored to the header it covers nothing while closed, and
// only while open does its panel overlap the first rows.
export default function DashboardOrderField({
  regionId,
  regionCode,
  products,
  launchedProductIds,
}: {
  regionId?: string;
  regionCode: string;
  products: { id: string; name: string }[];
  launchedProductIds: Set<string>;
}) {
  const qc = useQueryClient();
  const { data: configs } = useRegionConfigs(regionId);
  const [open, setOpen] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // A click anywhere else closes the panel, like any other dropdown.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const initial = useMemo<Item[]>(() => {
    const cfgOf = (id: string) =>
      (configs ?? []).find((c) => c.product_id === id);
    const metaOf = (id: string) =>
      (cfgOf(id)?.metadata as Record<string, any>) ?? {};
    return products
      .filter(
        (p) => launchedProductIds.has(p.id) && metaOf(p.id).visible !== false,
      )
      .sort((a, b) => {
        const pa =
          typeof metaOf(a.id).priority === "number"
            ? metaOf(a.id).priority
            : Infinity;
        const pb =
          typeof metaOf(b.id).priority === "number"
            ? metaOf(b.id).priority
            : Infinity;
        return pa !== pb ? pa - pb : a.name.localeCompare(b.name);
      })
      .map((p) => ({
        id: p.id,
        name: p.name,
        thumbnail:
          (cfgOf(p.id)?.images as Record<string, any>)?.thumbnail ?? null,
      }));
  }, [configs, products, launchedProductIds]);

  const [order, setOrder] = useState<Item[]>(initial);
  useEffect(() => setOrder(initial), [initial]);

  // View-only can look at the order but not change it. Deliberately gated on
  // `write` (decision, 2026-09-18): this is not a preference local to the page.
  // It writes metadata.priority per region, which is the order product cards
  // appear in on every patient's dashboard — so first position is a merchandising
  // decision, not a layout one. The route requires `write` to match.
  const canWrite = useHasAccess("write");
  const save = useMutation({
    mutationFn: (next: Item[]) =>
      setRegionConfigPriorities(
        regionId!,
        next.map((it, i) => ({ product_id: it.id, priority: i + 1 })),
      ),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: ["product-region-config", "region", regionId],
      }),
    onError: (e: any) => {
      toast({
        title: "Error",
        description: e.response?.data?.message || "Failed to save order",
        variant: "destructive",
      });
      setOrder(initial);
    },
  });

  const drop = (to: number) => {
    if (dragIndex === null || dragIndex === to) return setDragIndex(null);
    const next = [...order];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(to, 0, moved);
    setDragIndex(null);
    setOrder(next);
    save.mutate(next);
  };

  if (!regionId) return null;

  const regionLabel = regionCode.toUpperCase();

  return (
    <div ref={rootRef} className="relative">
      {/* ── Toolbar button ─────────────────────────────────────────────────── */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={`inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition ${
          open
            ? "border-sidebar bg-sidebar/10 text-gray-900"
            : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
        }`}
        title={canWrite ? "Arrange dashboard order" : "Dashboard order"}
      >
        <LayoutGrid className="h-3.5 w-3.5 text-sidebar" />
        Order · {regionLabel}
        <span className="text-gray-400">{order.length}</span>
      </button>

      {/* ── Phone-frame panel, dropped below the button ────────────────────── */}
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-80 origin-top-right animate-in fade-in-0 zoom-in-95 duration-200">
          <div className="rounded-xl border border-gray-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
              <div className="flex items-center gap-1.5 text-sm font-medium">
                <LayoutGrid className="h-4 w-4 text-sidebar" />
                Dashboard order — {regionLabel}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Phone "screen" */}
            <div className="p-3">
              {order.length === 0 ? (
                <p className="py-6 text-center text-xs text-gray-400">
                  No launched, visible products in {regionLabel}.
                </p>
              ) : (
                <div className="max-h-80 space-y-1.5 overflow-y-auto rounded-2xl border border-gray-200 bg-gray-50 p-2">
                  {order.map((it, i) => (
                    <div
                      key={it.id}
                      draggable={canWrite}
                      onDragStart={() => setDragIndex(i)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => canWrite && drop(i)}
                      onDragEnd={() => setDragIndex(null)}
                      className={`flex ${canWrite ? "cursor-grab" : "cursor-default"} select-none items-center gap-2 rounded-lg border bg-white px-2 py-1.5 shadow-sm ${
                        dragIndex === i
                          ? "border-sidebar opacity-50"
                          : "border-gray-200"
                      }`}
                    >
                      <GripVertical className="h-4 w-4 shrink-0 text-gray-300" />
                      <span className="w-4 shrink-0 text-center text-[11px] text-gray-400">
                        {i + 1}
                      </span>
                      {it.thumbnail ? (
                        <img
                          src={it.thumbnail}
                          alt=""
                          className="h-6 w-6 shrink-0 rounded object-cover"
                        />
                      ) : (
                        <div className="h-6 w-6 shrink-0 rounded bg-sidebar/10" />
                      )}
                      <span className="min-w-0 flex-1 truncate text-sm">
                        {it.name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {canWrite && (
                <p className="pt-2 text-center text-[11px] text-gray-400">
                  Drag to reorder · saves automatically
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
